import { IconChevronRight } from 'central-icons/IconChevronRight';
import { IconCrossSmall } from 'central-icons/IconCrossSmall';

import type { User } from '@revelio/data-access';
import { Button, IconButton } from '@revelio/ui';

import type { StatusDetails } from './impersonationModal.utils';
import styles from './impersonationSearch.module.css';
import { StatusBadge } from './StatusBadge';
import { UserImpersonationSearch } from './UserImpersonationSearch';

interface ImpersonationSearchCardProps {
  selectedUser: Partial<User> | null | undefined;
  activeStatus: StatusDetails;
  trialStatus: StatusDetails;
  onSelectUser: (user: Partial<User>) => void;
  onClearSelection: () => void;
  onEditOverrides: () => void;
}

export function ImpersonationSearchCard({
  selectedUser,
  activeStatus,
  trialStatus,
  onSelectUser,
  onClearSelection,
  onEditOverrides,
}: ImpersonationSearchCardProps) {
  return (
    <div
      className={styles.searchColumn}
      data-testid="search-user-to-impersonate"
    >
      <div>
        <span className={styles.hint}>
          Search for a user to impersonate across client groups.
        </span>
        <UserImpersonationSearch onSelectUser={onSelectUser} />
      </div>

      {selectedUser && (
        <div className={styles.selectedCard}>
          <div className={styles.selectedHeader}>
            <div className={styles.selectedIdentity}>
              <div className={styles.selectedNameRow}>
                <span className={styles.selectedBadge}>Selected</span>
                <span className={`${styles.selectedName} ${styles.truncate}`}>
                  {selectedUser.name || selectedUser.email || 'Unknown user'}
                </span>
              </div>
              <span className={`${styles.selectedMeta} ${styles.truncate}`}>
                {selectedUser.email || selectedUser.username || 'No email'}
                {selectedUser.client_name
                  ? ` · Client Group: ${selectedUser.client_name}`
                  : ''}
              </span>
            </div>
            <div className={styles.selectedActions}>
              {/* Status badges stay semantic (green = active, red = expired):
                  signal, not decoration. */}
              <StatusBadge colorScheme={trialStatus.colorScheme}>
                {trialStatus.label}
              </StatusBadge>
              <StatusBadge colorScheme={activeStatus.colorScheme}>
                {activeStatus.label}
              </StatusBadge>
              <IconButton
                aria-label="Clear selected user"
                icon={<IconCrossSmall size={12} ariaHidden={true} />}
                variant="ghost"
                size="sm"
                onClick={onClearSelection}
                data-testid="clear-selected-user-button"
              />
            </div>
          </div>

          <div className={styles.selectedFooter}>
            <Button
              variant="ghost"
              size="sm"
              rightIcon={<IconChevronRight size={14} ariaHidden={true} />}
              onClick={onEditOverrides}
              data-testid="edit-search-overrides-button"
            >
              Edit overrides
            </Button>
          </div>
        </div>
      )}
    </div>
  );
}
